import type {
  AgentReportEntity,
  ChatMessageEntity,
} from '@saaga/shared/store/slices/chat-message-entity.interface'
import { useAppSelector } from '@saaga/shared/store/store'
import { useEffect, useRef } from 'react'
import type { ReportClickMessage } from '@/components/chat/chat-message.interface'

interface UseAutoOpenReportParams {
  onReportClick: (message: ReportClickMessage) => void
}

export const useAutoOpenReport = ({ onReportClick }: UseAutoOpenReportParams): void => {
  const openedIdSetRef = useRef(new Set<string>())
  const entities = useAppSelector((s) => s.chat.messageEntities)
  const lastReportCompletedAt = useAppSelector((s) => s.chat.lastReportCompletedAt)
  const activeConversationId = useAppSelector((s) => s.chat.activeConversationId)

  useEffect(() => {
    if (!lastReportCompletedAt || !activeConversationId) return
    const report = findCompletedReport(entities, openedIdSetRef.current, activeConversationId)
    if (!report) return

    openedIdSetRef.current.add(report.id)
    onReportClick(entityToReportMessage(report))
  }, [lastReportCompletedAt, entities, onReportClick, activeConversationId])
}

const entityToReportMessage = (entity: AgentReportEntity): ReportClickMessage => ({
  id: entity.id,
  reportId: entity.reportId,
  content: entity.reportContent || entity.content || '',
  timestamp: entity.timestamp,
  participant: entity.participant,
  reportVersion: entity.reportVersion,
  isMasterArtifact: entity.isMasterArtifact,
  reportFormat: entity.reportFormat,
})

const findCompletedReport = (
  entities: Record<string, ChatMessageEntity>,
  alreadyOpenedIdSet: Set<string>,
  conversationId: string
): AgentReportEntity | null => {
  for (const entity of Object.values(entities)) {
    if (entity.type !== 'agent-report') continue
    if (entity.conversationId !== conversationId) continue
    if (!entity.shouldAutoOpen) continue
    if (entity.isStreaming) continue
    if (alreadyOpenedIdSet.has(entity.id)) continue
    return entity
  }
  return null
}
